import { supabase } from '../utils/supabaseClient';

// Achievement definitions with icons, descriptions, and criteria
const achievements = [
  {
    id: '4af4ff95-24b1-4167-8d76-a4985994bab9',
    title: 'First Steps',
    description: 'Complete your first game',
    icon: '🎮',
    category: 'general',
    points: 10,
    criteria: { games_played: 1 },
  },
  {
    id: 'a46eeaf0-2267-4301-96ab-668eb3696016',
    title: 'Memory Master',
    description: 'Score 1000+ points in Memory Match game',
    icon: '🧠',
    category: 'memory',
    points: 50,
    criteria: { memory_score: 1000 },
  },
  {
    id: 'e34278cd-8193-4e14-9068-0f0c76e63ed7',
    title: 'Word Wizard',
    description: 'Complete 10 Word Scramble games',
    icon: '📝',
    category: 'word',
    points: 30,
    criteria: { word_games: 10 },
  },
  {
    id: 'c1cf1f13-b34b-4830-963b-00d381743e90',
    title: 'Pattern Recognition Pro',
    description: 'Reach level 10 in Pattern Memory game',
    icon: '📊',
    category: 'pattern',
    points: 40,
    criteria: { pattern_level: 10 },
  },
  {
    id: '9396fe00-f9b5-4efb-8214-acdda1322735',
    title: 'Math Magician',
    description: 'Solve 50 math problems correctly',
    icon: '🔢',
    category: 'math',
    points: 35,
    criteria: { math_problems: 50 },
  },
  {
    id: '49e857c4-f990-4124-a9c1-40b19ed52bb9',
    title: 'Lightning Fast',
    description: 'Average reaction time under 300ms',
    icon: '⚡',
    category: 'reflex',
    points: 45,
    criteria: { avg_reaction_time: 500, comparison: 'less' },
  },
  {
    id: '1faa1ee7-ec12-4d26-ae9c-4bc0fad3d0d5',
    title: 'Consistent Player',
    description: 'Play games on 5 consecutive days',
    icon: '📅',
    category: 'general',
    points: 60,
    criteria: { consecutive_days: 5 },
  },
  {
    id: '7077538c-b5da-41f7-a2af-73ce3c75b621',
    title: 'All-Rounder',
    description: 'Play all 5 game types at least once',
    icon: '🌟',
    category: 'general',
    points: 75,
    criteria: { game_types: 5 },
  },
  {
    id: '880a7f36-3862-41a4-a0ef-7b500308b02a',
    title: 'Perfect Score',
    description: 'Get a perfect score in any game',
    icon: '💯',
    category: 'general',
    points: 100,
    criteria: { perfect_score: true },
  },
  {
    id: 'fd07aedc-6fa5-4eec-a33a-ed155f192402',
    title: 'Early Bird',
    description: 'Play a game before 8 AM',
    icon: '🐦',
    category: 'general',
    points: 15,
    criteria: { early_play: true },
  },
];

// Get all achievement definitions
export const getAchievements = () => {
  return achievements;
};

// Get user's earned achievements
export const getUserAchievements = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_achievements')
      .select('*')
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error fetching user achievements:', error);
      throw error;
    }
    
    return { data };
  } catch (error) {
    console.error('Error in getUserAchievements:', error);
    throw error;
  }
};

// Get user progress towards achievements
export const getUserProgress = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
    
    return { data };
  } catch (error) {
    console.error('Error in getUserProgress:', error);
    throw error;
  }
};

// Award an achievement to a user
export const awardAchievement = async (userId, achievementId) => {
  try {
    const { data, error } = await supabase
      .from('user_achievements')
      .insert(
        { user_id: userId,
          achievement_id: achievementId,
          earned_at: new Date().toISOString(),
        }
      );
    
    if (error) {
      console.error('Error awarding achievement:', error);
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error in awardAchievement:', error);
    throw error;
  }
};

// Check if user has earned any new achievements
export const checkAchievements = async (userId, stats) => {
  try {
    const { data: userAchievements } = await getUserAchievements(userId);
    const earnedAchievementIds = userAchievements.map(a => a.achievement_id);
    
    const newAchievements = [];
    achievements.forEach(achievement => {
      // Skip if already earned
      if (earnedAchievementIds.includes(achievement.id)) {
        return;
      }
      
      // Check if criteria are met
      let criteriaMatch = true;
      Object.entries(achievement.criteria).forEach(([key, requiredValue]) => {
        if (!stats[key] && !achievement.criteria.comparison) {
          criteriaMatch = false;
          return;
        }
        if (achievement.criteria.comparison === 'less') {
          if (stats[key] > requiredValue) {
            criteriaMatch = false;
          }
        } else {
          if (stats[key] < requiredValue) {
            criteriaMatch = false;
          }
        }
      });
      if (criteriaMatch) {
        newAchievements.push(achievement);
        // Award the achievement
        awardAchievement(userId, achievement.id);
      }
    });
    
    return newAchievements;
  } catch (error) {
    console.error('Error in checkAchievements:', error);
    return [];
  }
};

export const setUserProgress = async (userId, stats) => {
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .upsert([{
        user_id: userId,
        ...stats,
      }], { onConflict: ['user_id'] }).select().maybeSingle();;
    
    if (error) {
      console.error('Error setting user progress:', error);
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error in setUserProgress:', error);
    throw error;
  }
};

export default {
  getAchievements,
  getUserAchievements,
  getUserProgress,
  setUserProgress,
  awardAchievement,
  checkAchievements,
};
